import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActionsSubject } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { DigitalProspectService } from './services/enterprise-events/digital-prospect.service';
import { v4 } from 'uuid';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { WindowRefService } from './services/window-ref.service';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { init as initFullStory } from '@fullstory/browser';
import { Subject } from 'rxjs';
import { RouteWatcherService } from './feature-modules/shared/services/route-watcher.service';
import { CHANGE } from './store/actions/router.action';
import { SegmentJsAnalyticsService } from './services/segment-js-analytics.service';
import { OneTrustService } from './services/one-trust.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'auth-frontend';
  funnelLoginUrl: string;
  readonly window;
  currentUrl$ = this.routeWatcherService.currentUrl$;
  currentLayoutType$ = this.routeWatcherService.currentLayoutType$;
  isExistingCustomer: boolean;
  private readonly unsubscribeSubject: Subject<boolean> = new Subject();

  constructor(
    private readonly windowRefService: WindowRefService,
    private readonly cookieService: CookieService,
    readonly router: Router,
    private readonly digitalProspectService: DigitalProspectService,
    private readonly actionsSubject: ActionsSubject,
    private readonly routeWatcherService: RouteWatcherService,
    private readonly segmentJsAnalyticsService: SegmentJsAnalyticsService,
    private readonly oneTrustService: OneTrustService
  ) {
    if (!sessionStorage.getItem('auth-session-id')) {
      sessionStorage.setItem('auth-session-id', v4());
    }

    initFullStory({ orgId: '1GWQQ', devMode: environment.sbx });
    this.funnelLoginUrl = environment.funnelLoginUrl;
    this.window = this.windowRefService.nativeWindow;
    this.isExistingCustomer = this.cookieService.check('be-auth');
  }

  async ngOnInit() {
    if (this.window.FS) {
      this.window.FS.setUserVars({
        'auth-session-id': sessionStorage.getItem('auth-session-id'),
      });
      this.window.FS.setUserVars({
        'auth-app-version': environment.appVersion,
      });
    } else {
      console.warn(`Fullstory is not enabled in ${environment.env} environment`);
    }

    try {
      await this.digitalProspectService.initAsync();
    } catch (err) {
      console.warn('Digital Prospect initialization failed; continuing without DPID', err);
    }
    await this.linkOneTrustProspectIdToDataSubjectId();
    this.segmentJsAnalyticsService.init();
    this.segmentJsAnalyticsService.setupSegmentJsAnalytics();

    this.actionsSubject.pipe(takeUntil(this.unsubscribeSubject), ofType(CHANGE)).subscribe(() => {
      this.digitalProspectService.extendSession();
    });

    this.setOptimizelyData();
  }

  private async linkOneTrustProspectIdToDataSubjectId(): Promise<void> {
    try {
      await this.oneTrustService.linkProspectIdToDataSubjectId();
    } catch (err) {
      console.warn('OneTrust initialization failed; continuing without linking DPID', err);
    }
  }

  /**
   * This is a temporary bridge to continue to set Optimizely data in the utag_data object after removing Tealium from the application.
   * It retrieves the Optimizely data from localStorage and sets it in the utag_data object which is read by an external Optimizely script.
   * This is necessary because the Optimizely data was previously set in the Tealium utag script, and we need to maintain the same structure for compatibility with existing analytics and tracking systems.
   * This method should be removed once we have a more permanent solution for handling Optimizely data in the application.
   */
  setOptimizelyData(): void {
    try {
      const optimizelyData = {};
      Object.keys(this.window.localStorage).forEach((localStorageKey) => {
        if (/^optimizely_data.*visitor_profile$/i.test(localStorageKey)) {
          optimizelyData[localStorageKey] = this.window.localStorage[localStorageKey];
          optimizelyData['visitor_profile'] = JSON.parse(optimizelyData[localStorageKey])['profile'] || {};

          this.window.utag_data = this.window.utag_data || {};
          this.window.utag_data.optimizely_experiment_names =
            optimizelyData['visitor_profile'].optimizely_experiment_names || '';
          this.window.utag_data.optimizely_variation_names =
            optimizelyData['visitor_profile'].optimizely_variation_names || '';
        }
      });
    } catch (e) {
      console.warn('Error populating Optimizely properties:', e);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeSubject.next(null);
    this.unsubscribeSubject.complete();
  }
}
